import ModalSurface from '../shared/ModalSurface';
import '../../styles/restScreens.css';
import React, { useMemo, useState } from 'react';

function formatAnnotation(annotation, index) {
    const x = Number.isFinite(annotation.xPercent) ? annotation.xPercent.toFixed(1) : '—';
    const y = Number.isFinite(annotation.yPercent) ? annotation.yPercent.toFixed(1) : '—';
    const createdAt = annotation.createdAt ? new Date(annotation.createdAt).toLocaleString() : '—';
    return `${index + 1}. ${x}% / ${y}% — ${annotation.text || 'No note'} (${createdAt})`;
}

function SupportEscalationModal({
    activeGrant,
    annotations = [],
    dark = false,
    onClose,
    onSubmit
}) {
    const [findings, setFindings] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    const annotationSummary = useMemo(
        () => annotations.map(formatAnnotation),
        [annotations]
    );

    const textClass = dark ? 'text-gray-100' : 'text-gray-900';
    const mutedClass = dark ? 'text-gray-400' : 'text-gray-600';
    const cardClass = 'bg-[var(--surface-raised)] border-[var(--surface-border)]';
    const inputClass = dark
        ? 'bg-gray-900 border-gray-600 text-gray-100 placeholder-gray-500'
        : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500';

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!findings.trim()) {
            setError('Findings are required before escalating to Engineering.');
            return;
        }

        setSubmitting(true);
        setError(null);
        const result = await onSubmit(findings.trim());
        if (result?.error) {
            setError(result.error.message || 'Support escalation failed.');
            setSubmitting(false);
            return;
        }
        setSubmitting(false);
        onClose();
    };

    return (
        <ModalSurface closeOnEscape={false} onClose={onClose} label="Escalate support session" overlayClassName="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-[70]" panelClassName={`max-w-2xl w-full rounded-2xl border shadow-2xl p-6 ${cardClass}`} panelProps={{"data-m6-layout":"support","data-m6-component":"SupportEscalationModal"}}>
                <div className="flex items-center justify-between pb-3 border-b border-gray-700">
                    <h3 className={`font-bold text-lg flex items-center gap-2 ${textClass}`}>
                        <span>🚨</span>
                        <span>Escalate Support Session</span>
                    </h3>
                    <button
                        type="button"
                        onClick={onClose}
                        className="ia-modal-desktop-close text-gray-400 hover:text-white font-bold"
                        disabled={submitting}
                    >
                        ✕
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="mt-4 space-y-4">
                    <div className={`rounded-lg border p-3 text-sm ${dark ? 'border-gray-700 bg-gray-900/60' : 'border-gray-200 bg-gray-50'}`}>
                        <div className={`font-semibold ${textClass}`}>
                            {activeGrant?.org_name || activeGrant?.org_id || 'Unknown organization'}
                        </div>
                        <div className={`mt-1 ${mutedClass}`}>
                            Grant reason: {activeGrant?.reason || '—'}
                        </div>
                    </div>

                    <div>
                        <div className={`text-xs font-semibold uppercase tracking-wider mb-2 ${textClass}`}>
                            Current session annotations
                        </div>
                        {annotationSummary.length > 0 ? (
                            <ul className={`max-h-32 overflow-y-auto rounded-lg border p-3 text-xs space-y-1 ${dark ? 'border-gray-700 bg-gray-900 text-gray-200' : 'border-gray-200 bg-gray-50 text-gray-700'}`}>
                                {annotationSummary.map((line, index) => (
                                    <li key={`annotation-${index}`}>{line}</li>
                                ))}
                            </ul>
                        ) : (
                            <div className={`rounded-lg border p-3 text-xs ${dark ? 'border-gray-700 bg-gray-900 text-gray-400' : 'border-gray-200 bg-gray-50 text-gray-500'}`}>
                                No annotations have been added in this session.
                            </div>
                        )}
                    </div>

                    <label className={`block text-sm font-medium ${textClass}`}>
                        Findings / notes for Engineering
                        <textarea
                            value={findings}
                            onChange={(event) => setFindings(event.target.value)}
                            required
                            rows={5}
                            maxLength={4000}
                            placeholder="Summarize what you found, what the tenant reported, and what Engineering should investigate."
                            className={`mt-1 w-full rounded-lg border px-3 py-2 ${inputClass}`}
                        />
                    </label>

                    {error && (
                        <div className="rounded-lg border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800">
                            {error}
                        </div>
                    )}

                    <div className="flex justify-end gap-3 pt-2">
                        <button
                            type="button"
                            onClick={onClose}
                            disabled={submitting}
                            className={`px-4 py-2 rounded-lg text-sm font-medium ${
                                dark ? 'bg-gray-700 text-gray-200 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={submitting || !findings.trim()}
                            className="px-4 py-2 rounded-lg text-sm font-semibold bg-red-600 hover:bg-red-700 text-white disabled:opacity-50 shadow"
                        >
                            {submitting ? 'Escalating…' : 'Create GitHub Issue'}
                        </button>
                    </div>
                </form>
            </ModalSurface>
    );
}

export default SupportEscalationModal;
